import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import RolePresetsAdminPanel from './RolePresetsAdminPanel';

const MODULES = [
    { value: 'financial', label: 'Financial' },
    { value: 'personnel', label: 'Personnel' }
];
const ROLES = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'editor', label: 'Editor' },
    { value: 'manager', label: 'Manager' }
];

// BL-164 — the platform-wide Owner/Admin/Assignable Admin tier, distinct
// from the Financial module grants above (BL-103's role_assignments).
const ORG_ROLE_LABELS = {
    owner: 'Owner',
    admin: 'Admin',
    assignable_admin: 'Assignable Admin'
};
const INVITE_ROLES = [
    { value: 'admin', label: 'Admin' },
    { value: 'assignable_admin', label: 'Assignable Admin' }
];
const emptyGrant = (memberId = '') => ({
    grantee_id: memberId,
    module: 'financial',
    entity_id: '',
    role: 'viewer'
});
const emptyInvite = () => ({
    email: '',
    role: 'admin',
    entityId: ''
});

function RolesPermissionsView({ userId, theme = 'light', areaFilter = '' }) {
    const dark = theme === 'dark';
    const [members, setMembers] = useState([]);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [seatSummary, setSeatSummary] = useState(null);
    const [grants, setGrants] = useState([]);
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingGrant, setEditingGrant] = useState(null);
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [form, setForm] = useState(emptyGrant());
    const [saving, setSaving] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState(emptyInvite());
    const [sendingInvite, setSendingInvite] = useState(false);
    const [inviteAction, setInviteAction] = useState('');
    const [transferTarget, setTransferTarget] = useState('');
    const [transferring, setTransferring] = useState(false);
    const [transferError, setTransferError] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        const [directoryResult, grantsResult, entitiesResult, seatResult] = await Promise.all([
            supabaseClient.rpc('org_member_directory'),
            supabaseClient
                .from('role_assignments')
                .select('id, org_id, module, entity_id, grantee_type, grantee_id, role, created_at')
                .eq('grantee_type', 'user')
                .in('module', MODULES.map((module) => module.value))
                .order('created_at', { ascending: false }),
            supabaseClient
                .from('entities')
                .select('id, name, parent_entity_id, display_label')
                .order('name'),
            supabaseClient.rpc('bl395a_org_seat_summary')
        ]);
        const orgId = directoryResult.data?.[0]?.org_id;
        const invitesResult = directoryResult.error || !orgId
            ? { data: [], error: null }
            : await supabaseClient
                .from('org_invites')
                .select('id, org_id, entity_id, email, role, invited_by, created_at, expires_at')
                .eq('org_id', orgId)
                .is('accepted_at', null)
                .is('revoked_at', null)
                .order('created_at', { ascending: false });
        const firstError = directoryResult.error || grantsResult.error || entitiesResult.error || invitesResult.error || seatResult.error;
        if (firstError) {
            setError(firstError.message);
            setMembers([]);
            setPendingInvites([]);
            setGrants([]);
            setEntities([]);
            setSeatSummary(null);
        } else {
            setMembers(directoryResult.data || []);
            setPendingInvites(invitesResult.data || []);
            setGrants(grantsResult.data || []);
            setEntities(entitiesResult.data || []);
            setSeatSummary(seatResult.data?.[0] || null);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData, userId]);

    const entityMap = useMemo(
        () => new Map(entities.map((e) => [e.id, e])),
        [entities]
    );
    const seatLimit = seatSummary?.seat_limit ?? null;
    const seatsUsed = Number(seatSummary?.seats_used ?? 0);
    const teamCapReached = !seatSummary || (seatLimit !== null && seatsUsed >= seatLimit);
    const entityLabel = (entityId) => {
        const entity = entityMap.get(entityId);
        if (!entity) return 'Organization-wide';
        const parent = entity.parent_entity_id ? entityMap.get(entity.parent_entity_id) : null;
        return parent ? `${parent.name} / ${entity.name}` : entity.name;
    };

    const updateMemberHubScope = async (targetUserId, newEntityId) => {
        setError(null);
        const orgId = members[0]?.org_id;
        let query = supabaseClient
            .from('org_members')
            .update({ entity_id: newEntityId || null })
            .eq('user_id', targetUserId);
        if (orgId) {
            query = query.eq('org_id', orgId);
        }
        const { error: updateErr } = await query;
        if (updateErr) {
            setError(updateErr.message);
        } else {
            await loadData();
        }
    };

    const membersById = useMemo(
        () => new Map(members.map((member) => [member.id, member])),
        [members]
    );

    const grantsByMember = useMemo(() => {
        const grouped = new Map();
        grants.forEach((grant) => {
            const memberGrants = grouped.get(grant.grantee_id) || [];
            memberGrants.push(grant);
            grouped.set(grant.grantee_id, memberGrants);
        });
        return grouped;
    }, [grants]);

    // BL-164 Gate 4b — ownership transfer. Only the current Owner sees the control.
    const currentOwner = useMemo(() => members.find((member) => member.role === 'owner') || null, [members]);
    const myRole = useMemo(() => membersById.get(userId)?.role || null, [membersById, userId]);
    const isOwner = myRole === 'owner';
    const transferCandidates = useMemo(
        () => members.filter((member) => member.id !== userId),
        [members, userId]
    );

    const transferOwnership = async (event) => {
        event.preventDefault();
        if (!transferTarget) return;
        if (!window.confirm('Transfer Owner to this person? You will drop to Admin.')) return;
        setTransferring(true);
        setTransferError(null);
        const { error: transferErr } = await supabaseClient.rpc('transfer_ownership', {
            p_new_owner_user_id: transferTarget
        });
        if (transferErr) {
            setTransferError(transferErr.message);
        } else {
            setTransferTarget('');
            await loadData();
        }
        setTransferring(false);
    };

    const openAdd = (memberId = '') => {
        setEditingGrant(null);
        setForm(emptyGrant(memberId));
        setShowGrantModal(true);
    };

    const openEdit = (grant) => {
        setEditingGrant(grant);
        setForm({
            grantee_id: grant.grantee_id,
            module: grant.module,
            entity_id: grant.entity_id || '',
            role: grant.role
        });
        setShowGrantModal(true);
    };

    const closeModal = () => {
        setEditingGrant(null);
        setForm(emptyGrant());
        setShowGrantModal(false);
    };

    const saveGrant = async (event) => {
        event.preventDefault();
        const orgId = members[0]?.org_id;
        if (!orgId) {
            setError('No organization member is available to receive a grant.');
            return;
        }

        setSaving(true);
        setError(null);
        const payload = {
            org_id: orgId,
            module: form.module,
            entity_id: form.entity_id || null,
            grantee_type: 'user',
            grantee_id: form.grantee_id,
            role: form.role,
            created_by: userId
        };
        const result = editingGrant
            ? await supabaseClient.from('role_assignments').update(payload).eq('id', editingGrant.id)
            : await supabaseClient.from('role_assignments').insert([payload]);

        if (result.error) {
            setError(result.error.message);
        } else {
            closeModal();
            await loadData();
        }
        setSaving(false);
    };

    const deleteGrant = async (grant) => {
        if (!window.confirm('Remove this permission grant?')) return;
        setError(null);
        const { error: deleteError } = await supabaseClient
            .from('role_assignments')
            .delete()
            .eq('id', grant.id);
        if (deleteError) {
            setError(deleteError.message);
        } else {
            await loadData();
        }
    };

    const postTeamInvite = async (payload) => {
        const { data: sessionData } = await supabaseClient.auth.getSession();
        const token = sessionData?.session?.access_token;
        if (!token) {
            throw new Error('You must be signed in to manage invitations.');
        }
        const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/team-invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify(payload)
        });
        const responsePayload = await response.json();
        if (!response.ok) {
            throw new Error(responsePayload?.error || 'Invitation action failed.');
        }
        return responsePayload;
    };

    const sendInvite = async (event) => {
        event.preventDefault();
        setSendingInvite(true);
        setError(null);
        try {
            await postTeamInvite({
                action: 'invite',
                email: inviteForm.email,
                role: inviteForm.role,
                entityId: inviteForm.entityId || null
            });
            setInviteForm(emptyInvite());
            setShowInviteModal(false);
            await loadData();
        } catch (inviteError) {
            setError(inviteError.message);
        } finally {
            setSendingInvite(false);
        }
    };

    const manageInvite = async (action, inviteId) => {
        if (action === 'revoke' && !window.confirm('Revoke this invitation?')) return;
        setInviteAction(`${action}:${inviteId}`);
        setError(null);
        try {
            await postTeamInvite({ action, inviteId });
            await loadData();
        } catch (actionError) {
            setError(actionError.message);
        } finally {
            setInviteAction('');
        }
    };

    const memberLabel = (member) => member.full_name || member.email || member.id;
    const scopeLabel = (grant) => grant.entity_id ? 'Company/Market-scoped' : 'Organization-wide';
    const cardClass = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const textClass = dark ? 'text-gray-100' : 'text-gray-900';
    const mutedClass = dark ? 'text-gray-400' : 'text-gray-600';
    const inputClass = dark
        ? 'bg-gray-700 border-gray-600 text-gray-100'
        : 'bg-white border-gray-300 text-gray-900';

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h2 className={`text-2xl font-bold ${textClass}`}>Permissions &amp; Access</h2>
                        <span className={`text-sm font-medium ${mutedClass}`}>
                            Admin seats: {seatsUsed}{seatLimit === null ? ' (unlimited)' : ` of ${seatLimit}`}
                        </span>
                    </div>
                    <p className={`mt-1 text-sm ${mutedClass}`}>Assign module access to people in this organization.</p>
                </div>
                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => openAdd()}
                        disabled={members.length === 0}
                        className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
                    >
                        + Add permission
                    </button>
                    <span title={teamCapReached ? 'The organization has reached its admin-seat limit.' : ''}>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            disabled={teamCapReached}
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium"
                        >
                            + Invite teammate
                        </button>
                    </span>
                </div>
            </div>

            {error && <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

            {!loading && members.length > 0 && (
                <div className={`mb-6 rounded-xl border overflow-hidden ${cardClass}`}>
                    <div className={`px-5 py-4 border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <h3 className={`font-semibold ${textClass}`}>Ownership</h3>
                    </div>
                    <div className="p-5">
                        <p className={`text-sm ${mutedClass}`}>
                            Owner: <span className={`font-medium ${textClass}`}>{currentOwner ? memberLabel(currentOwner) : 'Unknown'}</span>
                        </p>
                        {isOwner ? (
                            <form onSubmit={transferOwnership} className="mt-4 flex flex-wrap items-end gap-3">
                                <label className={`block text-sm font-medium ${textClass}`}>
                                    Transfer Owner to
                                    <select
                                        value={transferTarget}
                                        onChange={(event) => setTransferTarget(event.target.value)}
                                        className={`mt-1 rounded-lg border px-3 py-2 ${inputClass}`}
                                    >
                                        <option value="">Select a person</option>
                                        {transferCandidates.map((member) => (
                                            <option key={member.id} value={member.id}>
                                                {memberLabel(member)} ({ORG_ROLE_LABELS[member.role] || member.role})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <button
                                    type="submit"
                                    disabled={!transferTarget || transferring}
                                    className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                                >
                                    {transferring ? 'Transferring...' : 'Transfer Ownership'}
                                </button>
                                <p className={`w-full text-xs ${mutedClass}`}>You will drop to Admin once the transfer completes.</p>
                            </form>
                        ) : (
                            <p className={`mt-2 text-xs ${mutedClass}`}>Only the current Owner can transfer ownership.</p>
                        )}
                        {transferError && <p className="mt-3 text-sm text-red-600">{transferError}</p>}
                    </div>
                </div>
            )}

            <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
                <div className={`px-5 py-4 border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`font-semibold ${textClass}`}>People</h3>
                </div>
                {loading ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>Loading members and grants...</div>
                ) : members.length === 0 && pendingInvites.length === 0 ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>No organization members are available.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Name</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Email</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Role</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Status</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Company/Market Scope</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Financial grants</th>
                                    <th className={`px-5 py-3 text-right text-xs font-semibold uppercase ${mutedClass}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {members.map((member) => {
                                    const memberGrants = grantsByMember.get(member.id) || [];
                                    const assignedEntity = member.entity_id ? entityMap.get(member.entity_id) : null;
                                    return (
                                        <tr key={member.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                            <td className={`px-5 py-4 font-medium ${textClass}`}>{memberLabel(member)}</td>
                                            <td className={`px-5 py-4 text-sm ${mutedClass}`}>{member.email || 'No email available'}</td>
                                            <td className={`px-5 py-4 text-sm ${mutedClass}`}>{ORG_ROLE_LABELS[member.role] || member.role || '—'}</td>
                                            <td className={`px-5 py-4 text-sm ${mutedClass}`}>Active</td>
                                            <td className="px-5 py-4 text-sm">
                                                <select
                                                    value={member.entity_id || ''}
                                                    onChange={(e) => updateMemberHubScope(member.id, e.target.value)}
                                                    className={`text-xs rounded border px-2 py-1 ${inputClass}`}
                                                    title="Set member entity/hub scope (BL-181 Gate 5)"
                                                >
                                                    <option value="">Organization-wide</option>
                                                    {entities.map((ent) => (
                                                        <option key={ent.id} value={ent.id}>
                                                            {ent.name} {ent.display_label ? `(${ent.display_label})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-5 py-4">
                                                {memberGrants.length === 0 ? (
                                                    <span className={`text-sm ${mutedClass}`}>No grants</span>
                                                ) : (
                                                    <div className="flex flex-wrap gap-2">
                                                        {memberGrants.map((grant) => (
                                                            <button
                                                                key={grant.id}
                                                                onClick={() => openEdit(grant)}
                                                                className="rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800 hover:bg-orange-200"
                                                            >
                                                                {grant.module} · {grant.role} · {scopeLabel(grant)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <button onClick={() => openAdd(member.id)} className="text-sm font-medium text-orange-600 hover:text-orange-700">Add grant</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pendingInvites.map((invite) => (
                                    <tr key={invite.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                        <td className={`px-5 py-4 font-medium ${textClass}`}>Invitation</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{invite.email}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{ORG_ROLE_LABELS[invite.role] || invite.role}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>Invited (pending)</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{entityLabel(invite.entity_id)}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>No grants</td>
                                        <td className="px-5 py-4 text-right">
                                            <button
                                                onClick={() => manageInvite('resend', invite.id)}
                                                disabled={Boolean(inviteAction)}
                                                className="mr-3 text-sm font-medium text-orange-600 hover:text-orange-700 disabled:opacity-50"
                                            >
                                                {inviteAction === `resend:${invite.id}` ? 'Resending...' : 'Resend'}
                                            </button>
                                            <button
                                                onClick={() => manageInvite('revoke', invite.id)}
                                                disabled={Boolean(inviteAction)}
                                                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                                            >
                                                {inviteAction === `revoke:${invite.id}` ? 'Revoking...' : 'Revoke'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {!loading && grants.length > 0 && (
                <div className={`mt-6 rounded-xl border overflow-hidden ${cardClass}`}>
                    <div className={`px-5 py-4 border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <h3 className={`font-semibold ${textClass}`}>Grant matrix</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Person</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Module</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Scope</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Role</th>
                                    <th className={`px-5 py-3 text-right text-xs font-semibold uppercase ${mutedClass}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {grants.map((grant) => (
                                    <tr key={grant.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                        <td className={`px-5 py-4 ${textClass}`}>{memberLabel(membersById.get(grant.grantee_id) || { id: grant.grantee_id })}</td>
                                        <td className={`px-5 py-4 capitalize ${textClass}`}>{grant.module}</td>
                                        <td className={`px-5 py-4 ${mutedClass}`}>{scopeLabel(grant)}</td>
                                        <td className={`px-5 py-4 capitalize ${textClass}`}>{grant.role}</td>
                                        <td className="px-5 py-4 text-right">
                                            <button onClick={() => openEdit(grant)} className="mr-3 text-sm font-medium text-blue-600 hover:text-blue-700">Edit</button>
                                            <button onClick={() => deleteGrant(grant)} className="text-sm font-medium text-red-600 hover:text-red-700">Remove</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <RolePresetsAdminPanel userId={userId} theme={theme} areaFilter={areaFilter} />

            {showGrantModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
                    <div className={`w-full max-w-lg rounded-xl border p-6 shadow-xl ${cardClass}`} onClick={(event) => event.stopPropagation()}>
                        <h3 className={`text-xl font-bold ${textClass}`}>{editingGrant ? 'Edit permission' : 'Add permission'}</h3>
                        <form onSubmit={saveGrant} className="mt-5 space-y-4">
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Person
                                <select
                                    value={form.grantee_id}
                                    onChange={(event) => setForm({ ...form, grantee_id: event.target.value })}
                                    required
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    <option value="">Select a person</option>
                                    {members.map((member) => <option key={member.id} value={member.id}>{memberLabel(member)}{member.email ? ` (${member.email})` : ''}</option>)}
                                </select>
                            </label>
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Module
                                <select
                                    value={form.module}
                                    onChange={(event) => setForm({ ...form, module: event.target.value })}
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    {MODULES.map((module) => <option key={module.value} value={module.value}>{module.label}</option>)}
                                </select>
                            </label>
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Scope
                                <select
                                    value={form.entity_id}
                                    onChange={(event) => setForm({ ...form, entity_id: event.target.value })}
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    <option value="">Organization-wide</option>
                                    {entities.map((entity) => (
                                        <option key={entity.id} value={entity.id}>{entityLabel(entity.id)}</option>
                                    ))}
                                </select>
                            </label>
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Role
                                <select
                                    value={form.role}
                                    onChange={(event) => setForm({ ...form, role: event.target.value })}
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    {ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                                </select>
                            </label>
                            <p className={`text-xs ${mutedClass}`}>Financial access can be organization-wide or entity-scoped. Role presets and Team/Group defaults are managed below.</p>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={closeModal} className={`rounded-lg px-4 py-2 text-sm font-medium ${mutedClass}`}>Cancel</button>
                                <button type="submit" disabled={saving} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50">{saving ? 'Saving...' : 'Save permission'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showInviteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowInviteModal(false)}>
                    <div className={`w-full max-w-lg rounded-xl border p-6 shadow-xl ${cardClass}`} onClick={(event) => event.stopPropagation()}>
                        <h3 className={`text-xl font-bold ${textClass}`}>Invite teammate</h3>
                        <form onSubmit={sendInvite} className="mt-5 space-y-4">
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Email
                                <input
                                    type="email"
                                    value={inviteForm.email}
                                    onChange={(event) => setInviteForm({ ...inviteForm, email: event.target.value })}
                                    required
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                />
                            </label>
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Role
                                <select
                                    value={inviteForm.role}
                                    onChange={(event) => setInviteForm({ ...inviteForm, role: event.target.value })}
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    {INVITE_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
                                </select>
                            </label>
                            <label className={`block text-sm font-medium ${textClass}`}>
                                Branch / entity scope
                                <select
                                    value={inviteForm.entityId}
                                    onChange={(event) => setInviteForm({ ...inviteForm, entityId: event.target.value })}
                                    className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                                >
                                    <option value="">Organization-wide</option>
                                    {entities.map((entity) => (
                                        <option key={entity.id} value={entity.id}>{entityLabel(entity.id)}</option>
                                    ))}
                                </select>
                            </label>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowInviteModal(false)} className={`rounded-lg px-4 py-2 text-sm font-medium ${mutedClass}`}>Cancel</button>
                                <button type="submit" disabled={sendingInvite} className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50">{sendingInvite ? 'Sending...' : 'Send invite'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RolesPermissionsView;
